/**
 * The `activity` wire contract — read directly off a `dh_ui` envelope's
 * `payload` whose `kind` is `"activity"`, never off raw JSON. Design doc §3.
 *
 * Three shapes share one envelope: `{state:"started"}`, `{state:"running",
 * steps:[...]}` and `{state:"finished", summary:{...}}`. `steps` is always an
 * upsert list — a step is identified by `id` (`"step:" + tool_call_id`, minted
 * by the backend) and its `phase` is monotonic (§3.4): `running` < `done` =
 * `failed`. That rule lives in `activityStore`, not here — this module only
 * reads the wire shape and never drops a step for looking odd. An unknown
 * `phase`/`icon` string is kept as-is; the row renders it with a default dot
 * rather than refusing to draw (§3.4, §4.5).
 */

export type ActivityStep = {
  id: string;
  /** `"running" | "done" | "failed"` on every real backend, but read as a
   *  bare string — an unrecognised value must not crash the row, only fall
   *  back to a neutral glyph (§3.4). */
  phase: string;
  text: string;
  icon: string;
  ms: number | null;
};

export type ActivitySummary = {
  steps: number;
  /** Wall-clock milliseconds the visitor waited for the turn — NOT the sum of
   *  the steps' own durations. On a parallel batch this is smaller than that
   *  sum (§2.10/§3.3): three tools at 500/650/700 ms land here as `ms: 750`. */
  ms: number;
  failed: number;
};

export type ActivityFrame =
  | { state: 'started' }
  | { state: 'running'; steps: ActivityStep[] }
  | { state: 'finished'; summary: ActivitySummary | null };

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function readStep(raw: unknown): ActivityStep | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  const text = str(raw.text);
  if (!id || !text) return null; // §3.3: `id`/`text` are always present
  return {
    id,
    phase: str(raw.phase) ?? 'running',
    text,
    icon: str(raw.icon) ?? 'dot',
    ms: num(raw.ms),
  };
}

function readSummary(raw: unknown): ActivitySummary | null {
  if (!isObject(raw)) return null;
  return {
    steps: num(raw.steps) ?? 0,
    ms: num(raw.ms) ?? 0,
    failed: num(raw.failed) ?? 0,
  };
}

/**
 * `payload` off a `kind: "activity"` envelope, or `null` when it is not a
 * shape this client understands — the caller drops the frame rather than
 * guessing (same discipline as `events.ts`'s `readMessage`).
 */
export function readActivityFrame(payload: unknown): ActivityFrame | null {
  if (!isObject(payload)) return null;
  const state = str(payload.state);
  if (state === 'started') return { state: 'started' };
  if (state === 'running') {
    const raw = Array.isArray(payload.steps) ? payload.steps : [];
    const steps = raw.map(readStep).filter((s): s is ActivityStep => s !== null);
    return { state: 'running', steps };
  }
  if (state === 'finished') return { state: 'finished', summary: readSummary(payload.summary) };
  return null;
}
